import {
    ALL_ANIMATED_MIME_TYPES,
    ALL_MIME_TYPES,
    MAX_FILE_COUNT,
    MAX_FILE_DURATION,
    MAX_FILE_SIZE,
    MAX_OUTPUT_VIDEO_DURATION,
    MIN_OUTPUT_VIDEO_DURATION,
    STILL_MIME_TYPES,
    VIDEO_MIME_TYPES,
} from '../constants'
import getLabelFromMimeTypes from '../utils/get-mime-types-label'
import { fileSizeToDisplayText, secondsToDisplayText } from '../utils/text-utils'
import CreationToolErrorFix from './error-fix'
import CreationToolOutputType from './output-type'

enum CreationToolError {
    InvalidMediaFile = 'CT_ERROR_INVALID_MEDIA_FILE',
    InvalidVideoFile = 'CT_ERROR_INVALID_VIDEO_FILE',
    InvalidStillFile = 'CT_ERROR_INVALID_STILL_FILE',
    InvalidAnimatedFile = 'CT_ERROR_INVALID_ANIMATED_FILE',
    InvalidVideoUrl = 'CT_ERROR_INVALID_VIDEO_URL',
    FileSizeTooLarge = 'CT_ERROR_FILE_SIZE_TOO_LARGE',
    VideoTooShort = 'CT_ERROR_VIDEO_TOO_SHORT',
    VideoTooLong = 'CT_ERROR_VIDEO_TOO_LONG',
    VideoDimensions = 'CT_ERROR_VIDEO_DIMENSIONS',
    VideoPrivate = 'CT_ERROR_VIDEO_PRIVATE',
    VideoLiveStream = 'CT_ERROR_VIDEO_LIVE_STREAM',
    VideoNeedsTrim = 'CT_ERROR_VIDEO_NEEDS_TRIM',
    VideoTrimError = 'CT_ERROR_VIDEO_TRIM_ERROR',
    NoAudio = 'CT_ERROR_NO_AUDIO',
    NoSuperCutFiles = 'CT_ERROR_NO_SUPERCUT_FILES',
    NoTransparency = 'CT_ERROR_NO_TRANSPARENCY',
    NotAnimated = 'CT_ERROR_NOT_ANIMATED',
    UploadFailed = 'CT_ERROR_UPLOAD_FAILED',
    UploadFailedAgain = 'CT_ERROR_UPLOAD_FAILED_AGAIN',
    TooManyFiles = 'CT_ERROR_TOO_MANY_FILES',
}

namespace CreationToolError {
    /**
     * Returns the error's display title.
     */
    export function title(error: CreationToolError): string {
        switch (error) {
            case CreationToolError.InvalidVideoFile:
            case CreationToolError.InvalidStillFile:
            case CreationToolError.InvalidAnimatedFile:
            case CreationToolError.InvalidMediaFile:
                return `Invalid file type!`
            case CreationToolError.InvalidVideoUrl:
                return `Invalid video URL!`
            case CreationToolError.FileSizeTooLarge:
                return `This file is too large!`
            case CreationToolError.VideoTooShort:
                return `This video is too short!`
            case CreationToolError.VideoTooLong:
            case CreationToolError.VideoNeedsTrim:
                return `This video is too long!`
            case CreationToolError.VideoPrivate:
                return `This video is private!`
            case CreationToolError.VideoLiveStream:
                return `This video may be a live stream!`
            case CreationToolError.VideoTrimError:
                return `Trimming failed!`
            case CreationToolError.VideoDimensions:
                return `Invalid video dimensions!`
            case CreationToolError.NoSuperCutFiles:
                return `No files found!`
            case CreationToolError.NoAudio:
                return `No audio detected!`
            case CreationToolError.NoTransparency:
                return `No transparency detected!`
            case CreationToolError.NotAnimated:
                return `No animation detected!`
            case CreationToolError.UploadFailed:
            case CreationToolError.UploadFailedAgain:
                return `Upload failed!`
            case CreationToolError.TooManyFiles:
                return `Too many files!`
        }
    }

    /**
     * Returns the error's description text.
     */
    export function description(error: CreationToolError): string {
        switch (error) {
            case CreationToolError.InvalidVideoFile:
                return `File must be ${getLabelFromMimeTypes(VIDEO_MIME_TYPES)}.`
            case CreationToolError.InvalidStillFile:
                return `File must be ${getLabelFromMimeTypes(STILL_MIME_TYPES)}.`
            case CreationToolError.InvalidAnimatedFile:
                return `File must be ${getLabelFromMimeTypes(ALL_ANIMATED_MIME_TYPES)}.`
            case CreationToolError.InvalidMediaFile:
                return `File must be ${getLabelFromMimeTypes(ALL_MIME_TYPES)}`
            case CreationToolError.InvalidVideoUrl:
                return `We were unable to obtain enough information from this video URL.`
            case CreationToolError.VideoTooShort:
                return `Clips must be at least ${MIN_OUTPUT_VIDEO_DURATION} second long.`
            case CreationToolError.VideoTooLong:
            case CreationToolError.FileSizeTooLarge:
                return `GIFs & Videos have to be smaller than ${fileSizeToDisplayText(
                    MAX_FILE_SIZE
                )} and shorter than ${secondsToDisplayText(MAX_FILE_DURATION)}.`
            case CreationToolError.VideoNeedsTrim:
                return `GIFs & Clips must be ${secondsToDisplayText(MAX_OUTPUT_VIDEO_DURATION)} or shorter.`
            case CreationToolError.VideoPrivate:
                return `The owner of this video does not allow us to make GIFs.`
            case CreationToolError.VideoLiveStream:
                return `This video appears to have no duration and may be a live stream. This is not yet supported.`
            case CreationToolError.VideoTrimError:
                return `An error occurred while attempting to trim your video.`
            case CreationToolError.VideoDimensions:
                return `We were unable to upload this clip due to the video dimensions.`
            case CreationToolError.NoSuperCutFiles:
                return `We were unable to find any Super Cut files.`
            case CreationToolError.NoAudio:
                return `Audio is required to upload as a ${CreationToolOutputType.title(CreationToolOutputType.Video)}.`
            case CreationToolError.NoTransparency:
                return `Stickers must have transparent backgrounds.`
            case CreationToolError.NotAnimated:
                return `File must be animated.`
            case CreationToolError.UploadFailed:
            case CreationToolError.UploadFailedAgain:
                return `An error occurred when uploading your file.`
            case CreationToolError.TooManyFiles:
                return `Only ${MAX_FILE_COUNT} files or less are supported at one time.`
        }
    }

    /**
     * Returns the error's fix.
     */
    export function fix(error: CreationToolError): CreationToolErrorFix {
        switch (error) {
            case CreationToolError.NoAudio:
            case CreationToolError.NoTransparency:
            case CreationToolError.VideoTooShort:
            case CreationToolError.VideoDimensions:
                return CreationToolErrorFix.UploadAsGif
            case CreationToolError.VideoTooLong:
                return CreationToolErrorFix.Supercut
            case CreationToolError.VideoNeedsTrim:
                return CreationToolErrorFix.TrimVideo
            case CreationToolError.UploadFailed:
                return CreationToolErrorFix.RetryUpload
            default:
                return CreationToolErrorFix.SupportCenter
        }
    }

    /**
     * Returns the error's sticker id.
     */
    export function stickerId(error: CreationToolError): string {
        switch (error) {
            case CreationToolError.NoSuperCutFiles:
            case CreationToolError.VideoTooLong:
            case CreationToolError.VideoNeedsTrim:
                return 'g2iPmjRwOtrPYj34fl' // supercut scissors
            case CreationToolError.NoAudio:
                return 'USUIWSteF8DJoc5Snd' // thinking emoji
            case CreationToolError.InvalidAnimatedFile:
            case CreationToolError.InvalidStillFile:
            case CreationToolError.InvalidVideoFile:
            case CreationToolError.InvalidVideoUrl:
            case CreationToolError.InvalidMediaFile:
                return 'iD6QiXTTAYrU5C3c89' // finger wag
            case CreationToolError.FileSizeTooLarge:
            case CreationToolError.TooManyFiles:
            case CreationToolError.VideoTooShort:
                return 'Iblv3w6yvj9DTMwCsJ' // small fingers
            default:
                return 'WprjTWyCWtfbJ11WEM' // gasp emoji
        }
    }

    /**
     * Returns an error from a server response.
     */
    export function fromServerResponse(error: Error): CreationToolError {
        const message = (error.message || '').toLowerCase()

        if (!message) return CreationToolError.UploadFailed

        // not animated
        if (message.indexOf('must be animated') > -1) {
            return CreationToolError.NotAnimated
        }

        // video too long
        if (message.indexOf('video duration must be <') > -1) {
            return CreationToolError.VideoTooLong
        }

        // video dimensions
        if (
            message.indexOf('video aspect ratio must be') > -1 ||
            message.indexOf('video width must be') > -1 ||
            message.indexOf('video height must be') > -1
        ) {
            return CreationToolError.VideoDimensions
        }

        return CreationToolError.UploadFailed
    }
}

export default CreationToolError
